<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { ModalName } from '@/constants/modals';
import { downloadLink, toReadableSize } from "@/helpers/file";
import { resolveUrl } from "@/helpers/s3";
import { capitalizeFirstLetter } from "@/helpers/string";

import type { ButtonAltProps } from "@/dsComponents/buttons/types";
import type { TableAltColumns, TableAltCustomElement, TableAltRows } from "@/dsComponents/TableAlt/types";
import type { ActiveProposalDB } from "@/modules/activeProposal/types";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import Modal from "@/dsComponents/modals/Modal.vue";
import { useModalStore } from "@/dsComponents/modals/modalStore";
import SectionTitleSeparator from "@/dsComponents/modals/subcomponents/SectionTitleSeparator.vue";
import TableAlt from "@/dsComponents/TableAlt/TableAlt.vue";

interface ShowActiveProposalEmailProps {
    open: boolean;
    activeProposal: ActiveProposalDB;
}

const props = withDefaults(defineProps<ShowActiveProposalEmailProps>(), {
    open: false,
});

const emit = defineEmits<(event: "close") => void>();

const modalStore = useModalStore();

const { t } = useI18n();
const translate = (key: string) => `page.activeProposition.modal.showActiveProposalEmailModal.${key}`;

const attachmentsColumns: TableAltColumns[] = [
    { field: "name", width: "auto" },
    { field: "format", width: "120px" },
    { field: "size", width: "120px" },
];

const attachmentsRowsActions: TableAltCustomElement<ButtonAltProps>[] = [
    {
        component: ButtonAlt,
        props: {
            iconOnly: "eye",
            variant: "ghost",
            size: "sm",
            type: "neutral",
        },
        action: async (rowIndex: number) => {
            const path = props.activeProposal.files?.[rowIndex]?.path ?? "";
            if (path) {
                modalStore.actions.open(ModalName.DocumentPreviewModal, {
                    path: path,
                    noActions: true,
                    onClose: () => modalStore.actions.close(ModalName.DocumentPreviewModal),
                });
            }
        },
    },
    {
        component: ButtonAlt,
        props: {
            iconOnly: "download",
            variant: "ghost",
            size: "sm",
            type: "neutral",
        },
        action: async (rowIndex: number) => {
            const signedUrl = await resolveUrl(props.activeProposal.files?.[rowIndex]?.path ?? "");
            if (signedUrl) downloadLink(signedUrl);
        },
    },
];

const attachmentsRows = computed<TableAltRows[]>(() => {
    return (
        props.activeProposal.files?.map((file) => {
            return {
                content: {
                    name: { text: { value: file.name ?? "" } },
                    format: { text: { value: capitalizeFirstLetter(file.extension ?? "") } },
                    size: { text: { value: toReadableSize(file.size ?? 0) ?? "" } },
                },
            };
        }) ?? []
    );
});
</script>

<template>
    <div>
        <Modal :open="true" :title="t(translate('title'))" width="640px" close @close="emit('close')">
            <div class="show-active-proposal-email">
                <div class="show-active-proposal-email__title-and-description">
                    <div class="show-active-proposal-email__title-and-description__label">
                        <span class="typo-xxs-medium">{{ t(translate("subject")) }}</span>
                    </div>
                    <ComboButton :label="props.activeProposal.emailContent?.subject ?? ''" size="sm" variant="ghost" cursor="default" no-hover />
                </div>
                <div class="show-active-proposal-email__aside">
                    <p class="show-active-proposal-email__aside__body typo-xs-medium">{{ props.activeProposal.emailContent?.message }}</p>
                </div>
                <template v-if="activeProposal?.files?.length">
                    <SectionTitleSeparator :title="t(translate('attachments'))" />
                    <div class="show-active-proposal-email__attachments">
                        <TableAlt
                            :columns="attachmentsColumns"
                            :rows="attachmentsRows"
                            :globalActions="{ rowActions: attachmentsRowsActions, width: '66px' }"
                            row-hover
                            cell-left-padding="4px"
                            cell-right-padding="4px"
                            left-padding="0"
                            right-padding="0"
                        />
                    </div>
                </template>
            </div>
        </Modal>
    </div>
</template>

<style lang="scss" scoped>
.show-active-proposal-email {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg, 12px);

    &__title-and-description {
        display: flex;
        align-items: center;
        align-self: stretch;

        &__label {
            display: flex;
            width: 100px;

            span {
                color: var(--text-sub-600, #494e61);
            }
        }
    }

    &__aside {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        align-self: stretch;

        &__body {
            padding: 5px 8px 5px 0;
            color: var(--text-main-900, #00061a);
            white-space: pre-wrap;
        }
    }
}
</style>
